<template>
  <PageContainer title="Turmas Teóricas" data-testid="page-container-turmas-teoricas">
    <div slot="header" class="header-actions" data-testid="header-actions">
      <InputDate :modelValue="date" @update:modelValue="$event => (date = $event)" :disabled="loading" data-testid="input-date" />

      <Button @handle:click="updateClasses" text="Atualizar turmas" color="primary" :disabled="loading" data-testid="btn-atualizar-turmas">
        <Sync :size="18" />
      </Button>
    </div>

    <div class="d-flex justify-content-center" v-if="!loaded" data-testid="loading-area">
      <LoadingSpinner data-testid="loading-spinner" />
    </div>

    <Box space="medium" v-else class="turmas-container" data-testid="turmas-container">
      <form @submit.prevent="handleSubmit" data-testid="buscar-cobrancas-form">
        <div class="inputs" data-testid="form-inputs">
          <div class="radio" data-testid="consult-radio-container">
            <span>Selecione o critério de consulta</span>

            <RadioButtons :options="consultOptions" name="consult" :modelValue="radioConsult"
              @update:modelValue="$event => radioConsult = $event" data-testid="consult-radio-buttons" />
          </div>

          <div class="inputs-row" data-testid="inputs-row">
            <Select :options="schemaOptions" legend="Auto escola" placeholder="Selecione uma auto escola"
              :modelValue="schema" @update:modelValue="$event => (schema = $event)" class="input-full"
              data-testid="select-autoescola" :disabled="loading" />

            <template v-if="radioConsult === 'period'">
              <Period class="input-full" legend="Data" :firstModelValue="initialDate" :secondModelValue="finalDate"
                @update:firstModelValue="$event => (initialDate = $event)"
                @update:secondModelValue="$event => (finalDate = $event)" :disabled="loading" data-testid="period-component" />
            </template>

            <template v-else>
              <Input legend="Agrupamento" placeholder="Digite o agrupamento da turma" :modelValue="agrupamento"
                @update:modelValue="$event => agrupamento = $event" class="input-full" data-testid="input-agrupamento" />
            </template>
          </div>

          <div class="inputs-row" data-testid="inputs-row" v-if="radioConsult == 'period'">
            <div class="radio">
              <span>Tipo de aula</span>

              <RadioButtons :options="typeOptions" name="type" :modelValue="type"
                @update:modelValue="$event => type = $event" data-testid="type-radio-buttons" />
            </div>
          </div>
        </div>

        <div class="buttons" data-testid="form-buttons">
          <Button type="button" @handle:click="clearInputs" text="Limpar" :fill="false" color="danger"
            :disabled="loading" data-testid="btn-limpar">
            <CloseCircleOutline :size="18" />
          </Button>

          <Button type="submit" text="Consultar" color="primary" :disabled="loading" data-testid="btn-consultar">
            <Magnify :size="18" />
          </Button>
        </div>
      </form>

      <div class="d-flex justify-content-center" v-if="loading" data-testid="loading-area">
        <LoadingSpinner data-testid="loading-spinner" />
      </div>

      <div class="table-container" v-else-if="turmas.length > 0 && !loading" data-testid="turmas-table-container">
        <Paginate :total-pages="paginationData.lastPage" :current-page="paginationData.currentPage"
          @page-changed="fetchTurmas" data-testid="paginate-turmas" />

        <Table
          :headers="['Agrupamento', 'Início', 'Fim', 'Alunos Regulares', 'Alunos Reposição', 'Virtual', 'Status', 'Ações']" data-testid="turmas-table">
          <tr v-for="turma in turmas" :key="turma.id_turmas_teoricas" :data-testid="'turmas-row-' + turma.id_turmas_teoricas">
            <td>{{ turma.id_agrupamento }}</td>
            <td>{{ utils.formatDate(turma.data_inicio) }}</td>
            <td>{{ utils.formatDate(turma.data_fim) }}</td>
            <td>{{ turma.quantidade_alunos }}</td>
            <td>{{ turma.reposicao_alunos.length }}</td>
            <td>{{ !turma.virtual ? 'Não' : 'Sim' }}</td>
            <td v-if="turma.finalizada && !turma.cancelada">Finalizada</td>
            <td v-else-if="!turma.finalizada && !turma.cancelada && turma.data_fim < '2022-01-12 00:00:00'">Realizada
            </td>
            <td v-else-if="turma.cancelada">Cancelada</td>
            <td v-else>Pendente</td>
            <td>
              <div class="actions">
                <div v-if="turma.virtual">
                  <IconButton @handle:click="joinClass(turma)" color="success" title="Acessar sala"
                    :disabled="loadingActions" data-testid="btn-acessar-sala">
                    <LoginVariant :size="19" />
                  </IconButton>
                </div>
                <div>
                  <IconButton @handle:click="openInfoModal(turma)" color="secondary" title="Detalhes"
                    :disabled="loadingActions" data-testid="btn-detalhes-turma">
                    <InformationOutline :size="19" />
                  </IconButton>
                </div>
              </div>
            </td>
          </tr>
        </Table>
      </div>
    </Box>

    <InfoModal ref="infoModal" data-testid="info-modal" />
  </PageContainer>
</template>

<script>
import utils from '../../../services/utils'

import PageContainer from 'core/PageContainer'
import Box from 'core/Box'
import InputDate from 'core/InputDate'
import SingleDate from 'core/SingleDate'
import Button from 'core/Button'
import Select from 'core/Select'
import LoadingSpinner from 'core/LoadingSpinner'
import RadioButtons from 'core/RadioButtons'
import Period from 'core/Period'
import Input from 'core/Input'
import Paginate from 'core/Paginate'
import Table from 'core/Table'
import IconButton from 'core/IconButton'
import Modal from 'core/Modal'
import Tabs from 'core/Tabs'

import Sync from 'icons/Sync'
import CloseCircleOutline from 'icons/CloseCircleOutline'
import Magnify from 'icons/Magnify'
import LoginVariant from 'icons/LoginVariant'
import InformationOutline from 'icons/InformationOutline'

import InfoModal from './InfoModal'

import router from '@/router'

export default {
  name: 'ListarPresenciais',
  components: {
    PageContainer,
    Box,
    InputDate,
    SingleDate,
    Button,
    Select,
    LoadingSpinner,
    RadioButtons,
    Period,
    Input,
    Paginate,
    Table,
    IconButton,
    Modal,
    Tabs,

    Sync,
    CloseCircleOutline,
    Magnify,
    LoginVariant,
    InformationOutline,

    InfoModal,
  },
  data() {
    return {
      utils: utils,
      date: '',
      radioConsult: 'period',
      schema: null,
      initialDate: '',
      finalDate: '',
      type: 'all',
      agrupamento: '',
      paginationData: {
        currentPage: 1,
        lastPage: 1,
        total: 1,
      },
      consultOptions: [
        { value: "period", label: "Período" },
        { value: "agrupamento", label: "Agrupamento" },
      ],
      schemaOptions: [],
      typeOptions: [
        { value: "all", label: "Todas" },
        { value: "virtual", label: "Virtual" },
        { value: "in_person", label: "Presencial" },
      ],
      turmas: [],
      loaded: false,
      loading: false,
      loadingActions: false,
    }
  },
  async created() {
    await this.fetchCfcs()

    this.loaded = true
  },
  methods: {
    async updateClasses() {
      if (!this.schema) {
        this.$toasted.show('Selecione uma auto escola.', {
          position: 'top-right',
        })

        return
      }
      if (!this.date) {
        this.$toasted.show('Nenhuma data selecionada!', {
          position: 'top-right',
        })

        return
      }

      this.loading = true

      const params = {
        schema: this.schema,
        date: this.date,
      }

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/getran/atualizar/turmas`, { params })
        .then(res => {
          this.$toasted.show(res.data.mensagem, {
            position: 'top-right',
          })

          this.initialDate = this.date
          this.finalDate = this.date

          this.fetchTurmas();
        })
        .catch(err => {
          this.$toasted.show(err.data.mensagem, {
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async fetchCfcs() {
      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/autoescola/todas`)
        .then((res) => {
          this.schemaOptions = res.body.dados.map(cfc => ({ value: cfc.tenant_schema, text: cfc.nome_fantasia }))
        })
        .catch((error) => {
          this.$toasted.show('Erro!' + error.body.mensagem, {
            position: 'top-right',
          })
        })
    },
    validateDados() {
      if (!this.schema) {
        this.$toasted.show('Selecione uma auto escola.', {
          position: 'top-right',
        })

        return false
      }

      if (this.radioConsult === 'agrupamento' && !this.agrupamento) {
        this.$toasted.show('Insira o agrupamento.', {
          position: 'top-right',
        })

        return false
      }

      if ((this.initialDate && !this.finalDate) || (!this.initialDate && this.finalDate)) {
        this.$toasted.show('Ambas as datas devem ser preenchidas.', {
          position: 'top-right',
        })

        return false
      }

      if (this.initialDate && this.finalDate) {
        if (this.initialDate > this.finalDate) {
          this.$toasted.show('A data inicial não pode ser maior que a data final.', {
            position: 'top-right',
          })

          return false
        }
      }

      return true
    },
    async handleSubmit() {
      if (this.validateDados()) {
        await this.fetchTurmas()
      }
    },
    async fetchTurmas(page = 1) {
      this.loading = true

      const params = {
        page,
        schema: this.schema,
      }

      if (this.radioConsult === 'period') {
        params.periodo = [this.initialDate, this.finalDate]
        params.type = this.type
      } else if (this.radioConsult === 'agrupamento') {
        params.agrupamento = this.agrupamento
      }

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/aulas/teoricas/turmas/presenciais`, { params })
        .then(res => {
          const dados = res.body.dados

          if (this.radioConsult === 'period') {
            if (dados.data.length === 0) {
              this.$toasted.show('Nenhuma turma encontrada.', {
                position: 'top-right',
              })

              return
            }

            this.turmas = dados.data

            this.paginationData = {
              currentPage: dados.current_page,
              lastPage: dados.last_page,
              total: dados.total,
            }
          } else if (this.radioConsult === 'agrupamento') {
            if (dados.length === 0 || dados[0] == null) {
              this.$toasted.show('Agrupamento não encontrado.', {
                position: 'top-right',
              })

              return
            }

            this.paginationData = {
              currentPage: 1,
              lastPage: 1,
              total: 1,
            }

            this.turmas = dados
          }
        })
        .catch(err => {
          this.$toasted.show(err.data.mensagem, {
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    clearInputs() {
      this.radioConsult = 'period'
      this.schema = null
      this.date = ''
      this.initialDate = ''
      this.finalDate = ''
      this.type = 'all'
    },
    async joinClass(turma) {
      this.loadingActions = true

      const data = {
        sala: turma.id_agrupamento,
        instrutor: true,
        nome: 'NOVOCFC MONITORAMENTO',
        id_aluno: localStorage.id,
        turma_teorica: turma.id_turma_teorica,
        monitoria: true
      }

      await this.$http.post(`${process.env.VUE_APP_URL_API}/ead/meet`, data)
        .then(res => {
          router.push({ path: '/ead/' + btoa(btoa(res.body.link)) })
        })
        .catch(err => {
          if (err.body.mensagem) {
            this.$toasted.show(err.body.mensagem, {
              position: 'top-right',
            })
          }
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    async openInfoModal(turma) {
      this.loadingActions = true

      await this.$refs.infoModal.open(this.schema, turma)

      this.loadingActions = false
    },
  },
}
</script>

<style lang="scss" scoped>
.header-actions {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
}

.turmas-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  form {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;

    .inputs {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      .radio {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        span {
          font-size: 1rem;
          font-weight: 600;
          line-height: 21.82px;
          color: #606264;
        }
      }

      .inputs-row {
        display: flex;
        flex-wrap: wrap;
        gap: 1.5rem;

        .input-full {
          flex: 1;
        }
      }
    }

    .buttons {
      align-self: flex-end;

      display: flex;
      gap: 0.5rem;
    }
  }

  .table-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .pagination-container {
      align-self: flex-end;
    }

    .actions {
      display: flex;
      align-items: center;

      div {
        padding: 0 0.5rem;
        display: flex;
        justify-content: center;

        &:not(:first-child) {
          border-left: 1px solid #ECECEC;
        }

        &:not(:last-child) {
          border-right: 1px solid #ECECEC;
        }
      }
    }
  }
}
</style>
